import { create } from 'zustand';
import type { AppManifestNavEntry } from '@/lib/apps-mounts';

/**
 * Runtime-pushed navigation contributed by an embedded app via the
 * `rumio:nav` postMessage (Apps Platform contract v1.6 §3).
 *
 * The Header component reads from this store while the user is inside an
 * app's mount path; if the entry for the active app is `undefined` or
 * `null`, the Header falls back to the app's manifest-declared nav.
 *
 * Cleared by `AppMountedRoute` when its iframe unmounts (or the route
 * leaves the mount prefix), so stale nav never leaks back to the host shell.
 */
interface AppNavState {
  /** Keyed by app `id` (the Rumio-side UUID, not client_id). */
  runtimeNavByAppId: Record<string, AppManifestNavEntry[] | null>;
  setRuntimeNav: (appId: string, items: AppManifestNavEntry[] | null) => void;
  clearRuntimeNav: (appId: string) => void;
}

export const useAppNavStore = create<AppNavState>()((set) => ({
  runtimeNavByAppId: {},
  setRuntimeNav: (appId, items) =>
    set((s) => ({
      runtimeNavByAppId: { ...s.runtimeNavByAppId, [appId]: items },
    })),
  clearRuntimeNav: (appId) =>
    set((s) => {
      if (!(appId in s.runtimeNavByAppId)) return s;
      const next = { ...s.runtimeNavByAppId };
      delete next[appId];
      return { runtimeNavByAppId: next };
    }),
}));
